// @flow

import type { Device, SensorType, SensorTypeName, SensorTypes } from '../_types'

export const AIR_QUALITY = 'airQuality'
export const ENVIRONMENTAL = 'environmental'

export const sensorsTypes: SensorTypes = {
  iaq: {
    indicator: 'index',
    order: 1,
    code: 301,
    name: 'iaq',
    shortName: 'IAQ',
    icon: 'iaq',
    extendedName: 'Indoor Air Quality',
    quasiExtendedName: 'INDOOR AIR QUALITY',
    category: AIR_QUALITY,
    values: {
      min: 0,
      reference: 250
    }
  },
  voc: {
    indicator: 'ppm',
    order: 101,
    code: 101,
    name: 'voc',
    shortName: 'VOC',
    icon: 'voc',
    extendedName: 'Volatile Organic Compounds',
    quasiExtendedName: 'VOCs',
    category: AIR_QUALITY,
    values: {
      min: 350,
      reference: 2600
    }
  },
  co: {
    indicator: 'ppm',
    order: 102,
    code: 102,
    name: 'co',
    shortName: 'CO',
    icon: 'co',
    extendedName: 'Carbon Monoxide',
    quasiExtendedName: 'CARBON MONOXIDE',
    category: AIR_QUALITY,
    values: {
      min: 0,
      reference: 50
    }
  },
  ch4: {
    indicator: 'ppm',
    order: 106,
    code: 103,
    name: 'ch4',
    shortName: 'CH₄',
    icon: 'ch4',
    extendedName: 'Methane Natural Gas',
    quasiExtendedName: 'METHANE NATURAL GAS',
    category: AIR_QUALITY,
    values: {
      min: 0,
      reference: 4000
    }
  },
  no2: {
    indicator: 'ppb',
    order: 104,
    code: 104,
    name: 'no2',
    shortName: 'NO₂',
    icon: 'no2',
    extendedName: 'Nitrogen Dioxide',
    quasiExtendedName: 'NITROGEN DIOXIDE',
    category: AIR_QUALITY,
    values: {
      min: 0,
      reference: 1000
    }
  },
  co2: {
    indicator: 'ppm',
    order: 101,
    code: 201,
    name: 'co2',
    shortName: 'CO₂',
    icon: 'co2',
    extendedName: 'Carbon Dioxide',
    quasiExtendedName: 'CARBON DIOXIDE',
    category: AIR_QUALITY,
    values: {
      min: 350,
      reference: 1600
    }
  },
  temp: {
    indicator: '°C',
    order: 401,
    code: 1,
    name: 'temp',
    shortName: 'Temperature',
    icon: 'temp',
    extendedName: 'Temperature',
    quasiExtendedName: 'TEMPERATURE',
    category: ENVIRONMENTAL,
    values: {
      min: -1000,
      reference: 1000
    }
  },
  humi: {
    indicator: '%',
    order: 402,
    code: 2,
    name: 'humi',
    shortName: 'Humidity',
    icon: 'humi',
    extendedName: 'Humidity',
    quasiExtendedName: 'HUMIDITY',
    category: ENVIRONMENTAL,
    values: {
      min: 0,
      reference: 100
    }
  },
  sound: {
    indicator: 'dB',
    order: 403,
    code: 3,
    name: 'sound',
    shortName: 'Noise',
    icon: 'sound',
    extendedName: 'Noise',
    quasiExtendedName: 'NOISE',
    category: ENVIRONMENTAL,
    values: {
      min: 0,
      reference: 1000
    }
  },
  press: {
    indicator: 'mBar',
    order: 404,
    code: 4,
    name: 'press',
    shortName: 'Pressure',
    icon: 'press',
    extendedName: 'Pressure',
    quasiExtendedName: 'PRESSURE',
    category: ENVIRONMENTAL,
    values: {
      min: 0,
      reference: 100000
    }
  },
  light: {
    indicator: 'lx',
    order: 405,
    code: 5,
    name: 'light',
    shortName: 'Light',
    icon: 'light',
    extendedName: 'Light',
    quasiExtendedName: 'LIGHT',
    category: ENVIRONMENTAL,
    values: {
      min: 0,
      reference: 10000000000
    }
  }
}

const allCategories = [
  AIR_QUALITY,
  ENVIRONMENTAL
]

const categoriesByName = {
  [AIR_QUALITY]: {
    name: AIR_QUALITY,
    extendedName: 'Air quality and pollutants',
    sensorsTypes: []
  },
  [ENVIRONMENTAL]: {
    name: AIR_QUALITY,
    extendedName: 'Environmental parameters',
    sensorsTypes: []
  }
}

export const sensorsCategories = {
  allNames: allCategories,
  byName: categoriesByName
}

export const getCategorizedDeviceSensorsTypes = (device: Device) => {
  const categorizedSensors = {}

  for (const categoryName of sensorsCategories.allNames) {
    categorizedSensors[categoryName] = []
  }

  for (const sensorTypeName of Object.keys(device.sensors)) {
    const sensorType = sensorsTypes[sensorTypeName]
    const category = sensorType.category
    categorizedSensors[category].push(sensorType.name)
  }

  for (const categoryName of sensorsCategories.allNames) {
    categorizedSensors[categoryName] = sensorTypeSort(categorizedSensors[categoryName])
  }

  return categorizedSensors
}

const sensorTypeSort = (sensorsArr: SensorTypeName[]) => {
  // $FlowFixMe
  return sensorsArr.sort((a, b) => sensorsTypes[a].order - sensorsTypes[b].order)
}

for (const sensorType: SensorType of Object.values(sensorsTypes)) {
  const category = sensorType.category
  categoriesByName[category].sensorsTypes.push(sensorType.name)
}
